import { Button, Device, Facilities, Facility, Vector2 } from "@app/shared";
import { FC, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Pages } from ".";
import { getFacilityCoordsHelper } from "../../helpers/coordsHelpers";
import { useFetch } from "../../hooks";
import { DashboardLayout } from "../layouts";
import { FacilityGroupDropdown, IconLink, Icons } from "../shared";
import { LayoutDesigner } from "../shared/LayoutDesigner/LayoutDesigner";
import { ShapeFilter } from "../shared/LayoutDesigner/ShapeFilter";
import { RequiresGoogleMaps } from "../shared/maps/RequiresGoogleMaps";
import { FacilityTab } from "./facility/facilityTabs";

export const FacilitiesMapView: FC = (props) => {
    const { id } = useParams();
    const [facilities, , fetchHelpers] = useFetch(() => Facilities.getByGroupId(id), [id]);
    const navigate = useNavigate();

    const baseCombinedFacility = useMemo(() => {
        if (!facilities?.length) return undefined;

        const baseFacility = facilities[0];
        return new Facility({
            ...baseFacility,
            id: `combined-${id}`,
            name: "All Facilities",
            organizationId: baseFacility.organizationId,
            facilityGroupId: id,
            devices: [],
            detectionZones: [],
            screenLines: [],
            laneLines: [],
            eventTriggers: [],
        });
    }, [facilities, id]);

    const createCombinedFacilityWithTransformedCoords = useMemo(() => {
        return (baseFacility: Facility) => {
            if (!facilities?.length || !baseFacility) return baseFacility;

            if (typeof google === 'undefined' || !google.maps) {
                console.warn('Google Maps API not loaded yet, using original coordinates');
                return baseFacility;
            }

            const combined = baseFacility;
            const combinedCoordsHelper = getFacilityCoordsHelper(combined);

            facilities.forEach(facility => {
                if (facility.devices?.length) {
                    const facilityCoordsHelper = getFacilityCoordsHelper(facility);

                    const transformedDevices = facility.devices.map(device => {
                        if (!device.positionX || !device.positionY) {
                            return device;
                        }

                        try {
                            const deviceLocalPosition = new Vector2({ x: device.positionX, y: device.positionY });
                            const deviceLatLng = facilityCoordsHelper.toLatLng(deviceLocalPosition);
                            const combinedPosition = combinedCoordsHelper.fromLatLng(deviceLatLng);

                            return new Device({
                                ...device,
                                positionX: combinedPosition.x,
                                positionY: combinedPosition.y
                            } as any);
                        } catch (error) {
                            console.warn('Failed to transform device coordinates:', error);
                            return { ...device };
                        }
                    });

                    combined.devices!.push(...transformedDevices);
                }

                // Todo: Transform all these shapes as well to the combined facility location
                if (facility.detectionZones) {
                    combined.detectionZones!.push(...facility.detectionZones);
                }
                if (facility.screenLines) {
                    combined.screenLines!.push(...facility.screenLines);
                }
                if (facility.laneLines) {
                    combined.laneLines!.push(...facility.laneLines);
                }
            });

            return combined;
        };
    }, [facilities]);

    // Filter to show everything on map but nothing in config (read-only)
    const shapeFilter = useMemo(() => new ShapeFilter({
        showDevicesOnMap: true,
        showDetectionZonesOnMap: true,
        showScreenLinesOnMap: true,
        showLaneLinesOnMap: true,
        showReferencePointOnMap: true,
        showDevicesInConfig: false,
        showDetectionZonesInConfig: false,
        showScreenLinesInConfig: false,
        showLaneLinesInConfig: false,
        showReferencePointInConfig: false,
    }), []);

    const handleGoToFacility = (facility: Facility) => {
        navigate(`${Pages.FacilityDetails.getUrl({ id: facility.id! })}?tab=${FacilityTab.Layout}`);
    };

    if (fetchHelpers.isLoading || !baseCombinedFacility) {
        return (
            <DashboardLayout
                centerChildren="Locations Map View"
                leftChildren={
                    <FacilityGroupDropdown
                        currentGroupId={id!}
                        onSelected={newId => {
                            navigate(Pages.FacilitiesMapView.getUrl({ id: newId }))
                        }}
                    />
                }>
                <div className="flex justify-center items-center min-h-[400px]">
                    <div>Loading locations...</div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout
            centerChildren="Locations Map View"
            leftChildren={
                <FacilityGroupDropdown
                    currentGroupId={id!}
                    onSelected={newId => {
                        navigate(Pages.FacilitiesMapView.getUrl({ id: newId }))
                    }}
                />
            }>

            <div className="flex flex-col h-full">
                <div className="flex gap-2 mb-3">
                    <IconLink Icon={Icons.ChevronLeft} onClick={() => navigate(Pages.ViewFacilityGroup.getUrl({ id: id! }))}>
                        Back to Locations List
                    </IconLink>
                </div>

                <div className="flex-1">
                    <RequiresGoogleMaps>
                        <FacilitiesMapContent
                            baseFacility={baseCombinedFacility!}
                            facilities={facilities!}
                            createCombinedFacility={createCombinedFacilityWithTransformedCoords}
                            shapeFilter={shapeFilter}
                            onGoToFacility={handleGoToFacility}
                        />
                    </RequiresGoogleMaps>
                </div>
            </div>
        </DashboardLayout>
    );
};

// Component that handles Google Maps-dependent functionality
const FacilitiesMapContent: FC<{
    baseFacility: Facility;
    facilities: Facility[];
    createCombinedFacility: (baseFacility: Facility) => Facility;
    shapeFilter: ShapeFilter;
    onGoToFacility: (facility: Facility) => void;
}> = ({ baseFacility, facilities, createCombinedFacility, shapeFilter, onGoToFacility }) => {

    // Now we can safely use Google Maps APIs since we're inside RequiresGoogleMaps
    const combinedFacility = useMemo(() => {
        return createCombinedFacility(baseFacility);
    }, [baseFacility, createCombinedFacility]);

    return (
        <div className="flex flex-col xl:flex-row h-full">
            <div className="flex-1">
                <LayoutDesigner
                    facility={combinedFacility}
                    allowEdit={false}
                    shapeFilter={shapeFilter}
                    hideWarning={true}
                    className="h-full"
                    showMapOnly={true}
                />
            </div>

            <div className="w-lg xl:w-80 xl:ml-4 mt-4 xl:mt-0">
                <div className="bg-white border rounded-lg p-4 h-full xl:overflow-y-auto">
                    <h3 className="text-lg font-semibold mb-4">Locations</h3>
                    <div className="space-y-3">
                        {facilities.map(facility => (
                            <div
                                key={facility.id}
                                className=""
                            >
                                <div className="flex items-start mb-2">
                                    <div>
                                        <h4 className="text-gray-900">{facility.name}</h4> {facility.deviceCount || 0} sensor(s)
                                    </div>
                                </div>


                                <Button
                                    onClick={() => onGoToFacility(facility)}
                                >
                                    Edit Location Layout
                                </Button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
